import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { API_BASE_URL, fetchJson } from './api';
import PlatformUsageAnalytics from './PlatformUsageAnalytics';
import { buildYearlyInvoiceRows, computeProgrammeDurationMonths } from './financialSchedule';

function CollapsibleSection({ title, defaultOpen = true, right, children }) {
    return (
        <Accordion
            defaultExpanded={defaultOpen}
            disableGutters
            sx={{
                mb: 2.5,
                borderRadius: 5,
                overflow: 'hidden',
                background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f6f9fc', 0.8)} 100%)`,
                border: `1px solid ${alpha('#ffffff', 0.72)}`,
                boxShadow: '0 22px 44px rgba(15, 23, 42, 0.08)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
                '&::before': { display: 'none' }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    minHeight: 78,
                    '& .MuiAccordionSummary-content': { my: 1.25 }
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                    <Box>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                            Operational Insight
                        </Typography>
                        <Typography fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>{title}</Typography>
                    </Box>
                    {right ? (
                        <Box sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 700 }}>
                            {right}
                        </Box>
                    ) : null}
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 } }}>{children}</AccordionDetails>
        </Accordion>
    );
}

function formatCurrency(val, currency = 'GBP') {
    try {
        const n = Number(val);
        if (Number.isNaN(n)) return '';
        return new Intl.NumberFormat('en-GB', { style: 'currency', currency: String(currency || 'GBP') }).format(n);
    } catch (e) { return val; }
}

function getAuthToken() {
    try {
        return String(localStorage.getItem('monsurv_auth_token') || '').trim();
    } catch {
        return '';
    }
}

function formatAwsCurrency(amount, currencyCode) {
    const code = String(currencyCode || '').trim().toUpperCase() || 'USD';
    // If Intl rejects an unknown code, fall back to plain number + code.
    try {
        return new Intl.NumberFormat('en-GB', { style: 'currency', currency: code }).format(Number(amount) || 0);
    } catch {
        const n = Number(amount) || 0;
        return `${n.toFixed(2)} ${code}`;
    }
}

function formatDateDdMmYyyy(dateLike) {
    const d = dateLike instanceof Date ? dateLike : new Date(dateLike || Date.now());
    if (!Number.isFinite(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

function createDefaultInvoicingSettings() {
    return {
        vat_reg_no: '',
        invoicing_company: '',
        address_lines: ['', '', '', ''],
        pay_terms: '',
        bank_acc_name: '',
        bank_acc_nr: '',
        bank_sort: '',
        invoice_sequence_next: 1000,
        template: {
            enabled: false,
            original_file_name: '',
            content_type: '',
            storage_bucket: '',
            storage_key: '',
            storage_region: '',
            size_bytes: 0,
            uploaded_at: '',
            uploaded_by: ''
        }
    };
}

function parseJobReference(refLike) {
    const raw = String(refLike || '').trim();
    if (!raw) return { raw: '', prefix: '', num: null };
    const m = raw.match(/^([a-z]+)\s*0*(\d+)$/i);
    if (!m) return { raw, prefix: raw.toUpperCase(), num: null };
    return { raw, prefix: String(m[1]).toUpperCase(), num: Number(m[2]) };
}

function compareJobReferences(aRef, bRef) {
    const a = parseJobReference(aRef);
    const b = parseJobReference(bRef);
    if (a.prefix !== b.prefix) return a.prefix.localeCompare(b.prefix);
    if (a.num !== null && b.num !== null && a.num !== b.num) return a.num - b.num;
    // Fallback for non-standard refs
    return a.raw.localeCompare(b.raw, undefined, { numeric: true, sensitivity: 'base' });
}

const premiumMetricCardSx = (accent) => ({
    p: { xs: 1.75, sm: 2 },
    minHeight: { xs: 132, sm: 146 },
    borderRadius: 4,
    position: 'relative',
    overflow: 'hidden',
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f7fbff', 0.78)} 100%)`,
    border: `1px solid ${alpha('#ffffff', 0.72)}`,
    boxShadow: '0 16px 32px rgba(15, 23, 42, 0.08)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 16,
        right: 16,
        height: 4,
        borderRadius: '0 0 6px 6px',
        background: accent
    }
});

const premiumPanelSx = {
    borderRadius: 5,
    overflow: 'hidden',
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8fbff', 0.78)} 100%)`,
    boxShadow: '0 24px 54px rgba(15, 23, 42, 0.10)',
    border: `1px solid ${alpha('#ffffff', 0.72)}`,
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)'
};

const premiumPanelHeaderSx = {
    px: { xs: 2, sm: 2.75 },
    pt: { xs: 2, sm: 2.4 },
    pb: { xs: 1.4, sm: 1.5 },
    borderBottom: `1px solid ${alpha('#0f172a', 0.06)}`,
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.52)} 0%, ${alpha('#f4f8fc', 0.22)} 100%)`
};

const premiumPanelBodySx = {
    px: { xs: 2, sm: 2.75 },
    py: { xs: 2, sm: 2.5 }
};

const dataTableSx = {
    minWidth: 720,
    '& .MuiTableCell-root': {
        borderColor: alpha('#0f172a', 0.06)
    }
};

const headCellSx = {
    py: 1.35,
    px: 1.6,
    backgroundColor: alpha('#f8fbff', 0.78),
    color: 'text.secondary',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.69rem',
    whiteSpace: 'nowrap'
};

const bodyCellSx = {
    px: 1.6,
    py: 1.45,
    fontSize: '0.92rem',
    verticalAlign: 'top'
};

const compactHeadCellSx = {
    ...headCellSx,
    px: 1.2,
    py: 0.9,
    fontSize: '0.64rem'
};

const compactBodyCellSx = {
    ...bodyCellSx,
    px: 1.2,
    py: 0.95,
    fontSize: '0.84rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle'
};

function toShortDate(value) {
    if (!value) return 'N/A';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function parseDateSafe(value) {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

function addMonths(dateLike, monthsToAdd) {
    const source = parseDateSafe(dateLike);
    if (!source) return null;
    const d = new Date(source);
    d.setMonth(d.getMonth() + Number(monthsToAdd || 0));
    return d;
}

function isInProgressInstruction(project) {
    const statusLike = String(
        project?.instruction_status
        || project?.instructionStatus
        || project?.project_status
        || project?.projectStatus
        || ''
    ).toLowerCase();
    return statusLike.includes('in-progress') || statusLike.includes('in progress');
}

const FEE_STATUS_FORECAST = 'forecast';
const FEE_STATUS_PAID = 'paid';
const FEE_STATUS_OVERDUE = 'overdue';
const FEE_STATUS_ISSUED = 'issued';
const FEE_STATUS_REMOVED = 'removed';

const FEE_STATUS_STYLES = {
    [FEE_STATUS_FORECAST]: {
        label: 'Forecast',
        bg: alpha('#fef3c7', 0.9),
        color: '#92400e'
    },
    [FEE_STATUS_PAID]: {
        label: 'Paid',
        bg: alpha('#dcfce7', 0.9),
        color: '#166534'
    },
    [FEE_STATUS_OVERDUE]: {
        label: 'Overdue',
        bg: alpha('#fee2e2', 0.9),
        color: '#991b1b'
    },
    [FEE_STATUS_ISSUED]: {
        label: 'Issued',
        bg: 'transparent',
        color: '#334155'
    }
};

function SummaryMetricCard({ icon, label, value, caption, accent }) {
    return (
        <Paper sx={premiumMetricCardSx(accent)}>
            <Stack spacing={1.2} sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {label}
                    </Typography>
                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', display: 'grid', placeItems: 'center', color: 'text.primary', backgroundColor: alpha('#ffffff', 0.58), border: `1px solid ${alpha('#ffffff', 0.52)}` }}>
                        {icon}
                    </Box>
                </Stack>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '1.9rem' }, lineHeight: 1, letterSpacing: '-0.04em' }}>
                    {value}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 240 }}>
                    {caption}
                </Typography>
            </Stack>
        </Paper>
    );
}

function PanelSection({ eyebrow, title, subtitle, right, children, bodySx }) {
    return (
        <Paper sx={premiumPanelSx}>
            <Box sx={premiumPanelHeaderSx}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                    <Box>
                        {eyebrow ? (
                            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                {eyebrow}
                            </Typography>
                        ) : null}
                        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                            {title}
                        </Typography>
                        {subtitle ? (
                            <Typography variant="body2" sx={{ mt: 0.35, color: 'text.secondary', maxWidth: 720 }}>
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Box>
                    {right ? <Box sx={{ flexShrink: 0 }}>{right}</Box> : null}
                </Stack>
            </Box>
            <Box sx={{ ...premiumPanelBodySx, ...(bodySx || {}) }}>{children}</Box>
        </Paper>
    );
}

export default function Financials() {
    const { onGoToInvoiceRevenue } = arguments.length > 0 && arguments[0] ? arguments[0] : {};
    const [projects, setProjects] = useState([]);
    const [programmeByRef, setProgrammeByRef] = useState(() => new Map());

    const [awsCosts, setAwsCosts] = useState(null);
    const [awsCostsLoading, setAwsCostsLoading] = useState(false);
    const [awsCostsError, setAwsCostsError] = useState('');

    async function refreshAwsCosts() {
        setAwsCostsLoading(true);
        setAwsCostsError('');
        try {
            const token = getAuthToken();
            if (!token) throw new Error('Missing dashboard auth token');
            const url = `${API_BASE_URL}/aws/costs/summary`;
            const json = await fetchJson(url, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });
            setAwsCosts(json);
        } catch (e) {
            setAwsCosts(null);
            setAwsCostsError(e?.message || 'Failed to load AWS costs');
        } finally {
            setAwsCostsLoading(false);
        }
    }

    useEffect(() => {
        fetchJson(`${API_BASE_URL}/key_project_data`, { method: 'GET' })
            .then((data) => {
                const rows = Array.isArray(data) ? data : [];
                // Map backend fields (snake_case) to the keys this page expects.
                const mapped = rows.map((p) => ({
                    ...p,
                    name: p.project_name ?? p.name ?? '',
                    reference: p.reference ?? p.project_reference ?? p.ref ?? '',
                    instructionDate: p.instruction_date ?? p.instructionDate ?? null,
                    initialReportFee: p.initial_report_fee ?? p.initialReportFee ?? null,
                    interimReportFee: p.interim_report_fee ?? p.interimReportFee ?? null,
                    // Default duration/start-on-site can be overridden by questionnaire output below.
                    durationMonths: p.project_duration ?? p.durationMonths ?? null,
                    startOnSiteDate: p.works_start_month ?? p.startOnSiteDate ?? null,
                }));
                setProjects(mapped);
            })
            .catch(() => setProjects([]));
    }, []);

    useEffect(() => {
        refreshAwsCosts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Enrich start date + duration using questionnaire output (INI Section 8 Programme)
    const refsKey = useMemo(
        () => projects.map((p) => String(p.reference || '').trim()).filter(Boolean).sort().join('|'),
        [projects]
    );

    useEffect(() => {
        let cancelled = false;

        async function run() {
            const refs = refsKey ? refsKey.split('|').filter(Boolean) : [];
            if (refs.length === 0) return;

            const results = await Promise.all(refs.map(async (ref) => {
                try {
                    const url = `${API_BASE_URL}/project/${encodeURIComponent(ref)}/initial-report?mode=surveyor`;
                    const ir = await fetchJson(url, { method: 'GET' });
                    const programmeRow = ir?.tables?.ini_section_8_programme?.[0] || null;
                    if (!programmeRow) return { ref, startOnSiteDate: null, durationMonths: null };
                    return {
                        ref,
                        startOnSiteDate: programmeRow?.start_date || null,
                        durationMonths: computeProgrammeDurationMonths(programmeRow)
                    };
                } catch (e) {
                    return null;
                }
            }));

            if (cancelled) return;
            setProgrammeByRef((prev) => {
                const next = new Map(prev);
                for (const r of results) {
                    if (!r) continue;
                    next.set(r.ref, { startOnSiteDate: r.startOnSiteDate, durationMonths: r.durationMonths });
                }
                return next;
            });
        }

        run();
        return () => { cancelled = true; };
    }, [refsKey]);

    const all = useMemo(() => {
        const enriched = projects.map((p) => {
            const ref = String(p.reference || '').trim();
            const prog = ref ? programmeByRef.get(ref) : null;
            if (!prog) return p;
            return {
                ...p,
                // Start date + duration should come from questionnaire output when available.
                startOnSiteDate: prog.startOnSiteDate || p.startOnSiteDate,
                durationMonths: (prog.durationMonths ?? p.durationMonths)
            };
        });

        enriched.sort((a, b) => compareJobReferences(a?.reference, b?.reference));
        return enriched;
    }, [projects, programmeByRef]);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    const nowYear = now.getFullYear();
    const currentMonthIndex = now.getMonth();
    const [selectedYear, setSelectedYear] = useState(nowYear);
    const [viewMode, setViewMode] = useState('monthly'); // 'monthly' | 'quarterly' | 'single'
    const [selectedMonthIndex, setSelectedMonthIndex] = useState(new Date().getMonth());
    const [selectedQuarter, setSelectedQuarter] = useState('all'); // 'all' or 0..3
    const [financialSubPage, setFinancialSubPage] = useState('fee_tracker');
    const [feeSearch, setFeeSearch] = useState('');
    const [feeTrackerMonthsAhead, setFeeTrackerMonthsAhead] = useState('3');
    const [interimStartGapMonths, setInterimStartGapMonths] = useState(3);
    const [serverFeeLines, setServerFeeLines] = useState([]);
    const [feeActionMenu, setFeeActionMenu] = useState({ anchorEl: null, feeIds: [], contextLabel: '', canCreateInvoice: false, primaryFee: null });
    const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
    const [createInvoiceDraft, setCreateInvoiceDraft] = useState({
        feeId: '',
        project: '',
        reference: '',
        project_address: '',
        invoice_date: '',
        invoice_nr: '',
        invoicee_type: 'Borrower',
        borrower_name: '',
        funder_name: '',
        invoicee: '',
        report_invoice: 'Initial',
        int_nr: '1',
        vat_rate: '20',
        base_fee: 0,
        vat_cost: 0,
        total: 0
    });
    const [invoicingSettings, setInvoicingSettings] = useState(createDefaultInvoicingSettings());
    const [invoicingLoading, setInvoicingLoading] = useState(false);
    const [invoicingSaving, setInvoicingSaving] = useState(false);
    const [invoicingUploading, setInvoicingUploading] = useState(false);
    const [invoicingError, setInvoicingError] = useState('');
    const [invoicingMessage, setInvoicingMessage] = useState('');
    const [createInvoiceSubmitting, setCreateInvoiceSubmitting] = useState(false);
    const [createInvoiceError, setCreateInvoiceError] = useState('');
    const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
    const [forecastFilter, setForecastFilter] = useState('all');

    useEffect(() => {
        let cancelled = false;

        async function loadInvoicingSettings() {
            setInvoicingLoading(true);
            setInvoicingError('');
            try {
                const token = getAuthToken();
                if (!token) throw new Error('Missing dashboard auth token');
                const json = await fetchJson(`${API_BASE_URL}/admin/settings/invoicing`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (cancelled) return;
                const incoming = json?.settings && typeof json.settings === 'object' ? json.settings : createDefaultInvoicingSettings();
                const addressLines = Array.isArray(incoming.address_lines) ? incoming.address_lines.slice(0, 10) : [];
                while (addressLines.length < 4) addressLines.push('');
                setInvoicingSettings({
                    ...createDefaultInvoicingSettings(),
                    ...incoming,
                    address_lines: addressLines,
                    template: {
                        ...createDefaultInvoicingSettings().template,
                        ...(incoming.template && typeof incoming.template === 'object' ? incoming.template : {})
                    }
                });
            } catch (e) {
                if (!cancelled) setInvoicingError(e?.message || 'Failed to load invoicing settings');
            } finally {
                if (!cancelled) setInvoicingLoading(false);
            }
        }

        loadInvoicingSettings();
        return () => { cancelled = true; };
    }, []);

    // compute a sensible range of years to choose from based on project dates
    const yearHints = (() => {
        let min = nowYear;
        let max = nowYear;
        all.forEach(p => {
            ['instructionDate', 'startOnSiteDate'].forEach(k => {
                if (p[k]) {
                    const d = new Date(p[k]);
                    if (!Number.isNaN(d.getTime())) {
                        min = Math.min(min, d.getFullYear());
                        max = Math.max(max, d.getFullYear());
                    }
                }
            });
        });
        // allow next year as well
        max = Math.max(max, nowYear) + 1;
        const out = [];
        for (let y = min; y <= max; y++) out.push(y);
        return out;
    })();

    function buildSchedule(p) {
        return buildYearlyInvoiceRows(p, selectedYear);
    }

    // build monthly schedules for the selected year, then aggregate according to viewMode
    const schedules = all.map(p => ({ project: p, rows: buildSchedule(p) }));

    function aggregateMonths(monthRow) {
        if (viewMode === 'monthly') return monthRow.slice();
        if (viewMode === 'quarterly') {
            if (selectedQuarter === 'all') {
                return [
                    monthRow.slice(0, 3).reduce((a, b) => a + b, 0),
                    monthRow.slice(3, 6).reduce((a, b) => a + b, 0),
                    monthRow.slice(6, 9).reduce((a, b) => a + b, 0),
                    monthRow.slice(9, 12).reduce((a, b) => a + b, 0),
                ];
            }
            const q = Number(selectedQuarter);
            const start = q * 3;
            return [monthRow.slice(start, start + 3).reduce((a, b) => a + b, 0)];
        }
        // single month
        const idx = selectedMonthIndex;
        return [monthRow[idx] || 0];
    }

    const schedulesAggregated = schedules.map(s => ({
        project: s.project,
        periods: aggregateMonths(s.rows.combined),
        initialPeriods: aggregateMonths(s.rows.initialRow),
        interimPeriods: aggregateMonths(s.rows.interimRow)
    }));

    const periodCount = schedulesAggregated.length ? schedulesAggregated[0].periods.length : (viewMode === 'quarterly' ? 4 : (viewMode === 'single' ? 1 : 12));
    const periodTotals = new Array(periodCount).fill(0);
    const initialPeriodTotals = new Array(periodCount).fill(0);
    const interimPeriodTotals = new Array(periodCount).fill(0);
    schedulesAggregated.forEach(s => s.periods.forEach((v, i) => periodTotals[i] += v));
    schedulesAggregated.forEach(s => s.initialPeriods.forEach((v, i) => initialPeriodTotals[i] += v));
    schedulesAggregated.forEach(s => s.interimPeriods.forEach((v, i) => interimPeriodTotals[i] += v));

    const selectedInitialTotal = initialPeriodTotals.reduce((a, b) => a + b, 0);
    const selectedInterimTotal = interimPeriodTotals.reduce((a, b) => a + b, 0);
    const selectedTotal = selectedInitialTotal + selectedInterimTotal;

    const portfolioCommittedValue = useMemo(() => all.reduce((sum, project) => {
        const initial = Number(project.initialReportFee) || 0;
        const interim = Number(project.interimReportFee) || 0;
        const duration = Math.max(0, Number(project.durationMonths) || 0);
        return sum + initial + (duration * interim);
    }, 0), [all]);

    const leadingProject = useMemo(() => {
        if (!all.length) return null;
        return all.reduce((best, project) => {
            const total = (Number(project.initialReportFee) || 0) + (Math.max(0, Number(project.durationMonths) || 0) * (Number(project.interimReportFee) || 0));
            if (!best || total > best.total) {
                return {
                    name: project.name || project.reference || 'Project',
                    reference: project.reference || '',
                    total
                };
            }
            return best;
        }, null);
    }, [all]);

    const averageCommittedValue = all.length ? portfolioCommittedValue / all.length : 0;
    const periodLabel = viewMode === 'monthly' ? `${selectedYear} (12M)` : viewMode === 'quarterly' ? `${selectedYear} (Quarterly)` : `${months[selectedMonthIndex]} ${selectedYear}`;
    const planningWindowLabel = viewMode === 'monthly'
        ? 'Rolling 12-month revenue view'
        : viewMode === 'quarterly'
            ? (selectedQuarter === 'all' ? 'Quarterly roll-up across the year' : `Focused view for Q${Number(selectedQuarter) + 1}`)
            : 'Single billing month focus';

    const invoicedFeeRows = useMemo(() => {
        const rows = [];
        all.forEach((project) => {
            const reference = String(project.reference || '').trim() || '—';
            const projectName = String(project.name || '').trim() || 'Untitled project';
            const initialFee = Math.max(0, Number(project.initialReportFee) || 0);
            const interimFee = Math.max(0, Number(project.interimReportFee) || 0);

            const initialInvoiceDate = project.initial_invoice_date
                || project.initialInvoiceDate
                || project.initial_invoice_issued_at
                || project.initial_report_issued_at
                || null;
            const initialInvoiced = Boolean(
                initialInvoiceDate
                || project.initial_fee_invoiced
                || project.initialFeeInvoiced
                || project.initial_invoice_number
            );

            if (initialFee > 0) {
                rows.push({
                    key: `${reference}-initial`,
                    project: projectName,
                    reference,
                    feeType: 'Initial',
                    amount: initialInvoiced ? initialFee : 0,
                    status: initialInvoiced ? 'Invoiced' : 'Not invoiced',
                    invoiceDate: toShortDate(initialInvoiceDate)
                });
            }

            const interimIssuedCountRaw = project.interim_reports_issued
                ?? project.interimReportsIssued
                ?? project.issued_interim_count
                ?? project.interim_invoiced_count
                ?? 0;
            const interimInvoiceDate = project.interim_invoice_date
                || project.interimInvoiceDate
                || project.latest_interim_invoice_date
                || project.issue_date
                || null;

            let interimIssuedCount = Math.max(0, Math.trunc(Number(interimIssuedCountRaw) || 0));
            if (interimIssuedCount === 0 && (project.interim_fee_invoiced || project.interimFeeInvoiced || interimInvoiceDate)) {
                interimIssuedCount = 1;
            }

            if (interimFee > 0) {
                rows.push({
                    key: `${reference}-interim`,
                    project: projectName,
                    reference,
                    feeType: 'Interim',
                    amount: interimIssuedCount > 0 ? (interimIssuedCount * interimFee) : 0,
                    status: interimIssuedCount > 0 ? 'Invoiced' : 'Not invoiced',
                    invoiceDate: toShortDate(interimInvoiceDate)
                });
            }
        });

        rows.sort((a, b) => compareJobReferences(a.reference, b.reference) || a.feeType.localeCompare(b.feeType));
        return rows;
    }, [all]);

    const forecastFeeRows = useMemo(() => {
        return schedulesAggregated
            .map((schedule) => {
                const initialForecast = schedule.initialPeriods.reduce((sum, n) => sum + (Number(n) || 0), 0);
                const interimForecast = schedule.interimPeriods.reduce((sum, n) => sum + (Number(n) || 0), 0);
                const totalForecast = initialForecast + interimForecast;
                const forecastType = initialForecast > 0 && interimForecast > 0
                    ? 'Mixed'
                    : initialForecast > 0
                        ? 'Initial'
                        : interimForecast > 0
                            ? 'Interim'
                            : 'None';

                return {
                    key: `${schedule.project.reference || schedule.project.name}-forecast`,
                    project: schedule.project.name || 'Untitled project',
                    reference: schedule.project.reference || '—',
                    forecastType,
                    initialForecast,
                    interimForecast,
                    totalForecast,
                    window: periodLabel
                };
            })
            .sort((a, b) => compareJobReferences(a.reference, b.reference));
    }, [periodLabel, schedulesAggregated]);

    const searchNeedle = String(feeSearch || '').trim().toLowerCase();
    const filteredInvoicedRows = useMemo(() => {
        return invoicedFeeRows.filter((row) => {
            const matchesSearch = !searchNeedle
                || row.project.toLowerCase().includes(searchNeedle)
                || row.reference.toLowerCase().includes(searchNeedle);
            const matchesStatus = invoiceStatusFilter === 'all'
                || (invoiceStatusFilter === 'invoiced' && row.status === 'Invoiced')
                || (invoiceStatusFilter === 'not_invoiced' && row.status === 'Not invoiced');
            return matchesSearch && matchesStatus;
        });
    }, [invoiceStatusFilter, invoicedFeeRows, searchNeedle]);

    const filteredForecastRows = useMemo(() => {
        return forecastFeeRows.filter((row) => {
            const matchesSearch = !searchNeedle
                || row.project.toLowerCase().includes(searchNeedle)
                || row.reference.toLowerCase().includes(searchNeedle);
            const matchesType = forecastFilter === 'all'
                || (forecastFilter === 'with_value' && row.totalForecast > 0)
                || (forecastFilter === 'none' && row.totalForecast <= 0)
                || (forecastFilter === 'initial' && row.forecastType === 'Initial')
                || (forecastFilter === 'interim' && row.forecastType === 'Interim')
                || (forecastFilter === 'mixed' && row.forecastType === 'Mixed');
            return matchesSearch && matchesType;
        });
    }, [forecastFeeRows, forecastFilter, searchNeedle]);

    const visibleInvoicedTotal = filteredInvoicedRows.reduce((sum, row) => sum + row.amount, 0);
    const visibleForecastTotal = filteredForecastRows.reduce((sum, row) => sum + row.totalForecast, 0);

    const forecastByReference = useMemo(() => {
        const map = new Map();
        forecastFeeRows.forEach((row) => {
            const key = String(row.reference || '').trim();
            if (!key) return;
            map.set(key, Number(row.totalForecast) || 0);
        });
        return map;
    }, [forecastFeeRows]);

    const invoicedByReference = useMemo(() => {
        const map = new Map();
        invoicedFeeRows.forEach((row) => {
            const key = String(row.reference || '').trim();
            if (!key) return;
            const entry = map.get(key) || { total: 0, lineCount: 0, invoicedLineCount: 0 };
            entry.total += Number(row.amount) || 0;
            entry.lineCount += 1;
            if (row.status === 'Invoiced') entry.invoicedLineCount += 1;
            map.set(key, entry);
        });
        return map;
    }, [invoicedFeeRows]);

    const trackerRows = useMemo(() => {
        return all
            .map((project) => {
                const reference = String(project.reference || '').trim() || '—';
                const initial = Math.max(0, Number(project.initialReportFee) || 0);
                const interim = Math.max(0, Number(project.interimReportFee) || 0);
                const duration = Math.max(0, Number(project.durationMonths) || 0);
                const committed = initial + (interim * duration);
                const forecast = Number(forecastByReference.get(reference) || 0);
                const invoicedInfo = invoicedByReference.get(reference) || { total: 0, lineCount: 0, invoicedLineCount: 0 };
                const invoiced = Number(invoicedInfo.total) || 0;
                const outstanding = Math.max(0, committed - invoiced);

                let status = 'Not invoiced';
                if (invoiced > 0 && outstanding <= 0) status = 'Fully invoiced';
                else if (invoiced > 0) status = 'Partially invoiced';

                return {
                    key: `${reference}-${project.name || 'project'}`,
                    project: project.name || 'Untitled project',
                    reference,
                    initial,
                    interim,
                    forecast,
                    invoiced,
                    outstanding,
                    status
                };
            })
            .sort((a, b) => compareJobReferences(a.reference, b.reference));
    }, [all, forecastByReference, invoicedByReference]);

    const filteredTrackerRows = useMemo(() => trackerRows.filter((row) => {
        if (!searchNeedle) return true;
        return row.project.toLowerCase().includes(searchNeedle)
            || row.reference.toLowerCase().includes(searchNeedle)
            || row.status.toLowerCase().includes(searchNeedle);
    }), [trackerRows, searchNeedle]);

    const trackerOutstandingTotal = filteredTrackerRows.reduce((sum, row) => sum + row.outstanding, 0);

    useEffect(() => {
        let cancelled = false;
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        async function run() {
            try {
                const qs = new URLSearchParams({
                    selected_year: String(selectedYear),
                    interim_gap_months: String(interimStartGapMonths)
                });
                const json = await fetchJson(`${API_BASE_URL}/api/financials/fee-lines?${qs.toString()}`, {
                    method: 'GET',
                    headers
                });
                if (cancelled) return;
                setServerFeeLines(Array.isArray(json?.lines) ? json.lines : []);
            } catch {
                if (!cancelled) setServerFeeLines([]);
            }
        }

        run();
        return () => { cancelled = true; };
    }, [interimStartGapMonths, selectedYear]);

    const feeLines = useMemo(() => {
        return (Array.isArray(serverFeeLines) ? serverFeeLines : []).map((line) => ({
            ...line,
            date: parseDateSafe(line.date) || new Date()
        }));
    }, [serverFeeLines]);

    const resolvedFeeLines = useMemo(() => {
        return feeLines
            .map((line) => {
                return {
                    ...line,
                    status: line.status || line.baseStatus,
                    periodLabel: toShortDate(line.date)
                };
            })
            .filter(Boolean);
    }, [feeLines]);

    const monthlyTrackerRows = useMemo(() => {
        return all.map((project) => {
            const reference = String(project.reference || '').trim() || '—';
            const projectName = String(project.name || '').trim() || 'Untitled project';
            const rowLines = resolvedFeeLines.filter((line) => line.reference === reference && line.date.getFullYear() === selectedYear);

            const monthCells = new Array(12).fill(null).map(() => ({ amount: 0, feeIds: [], statuses: [] }));
            rowLines.forEach((line) => {
                const idx = line.date.getMonth();
                monthCells[idx].amount += Number(line.invoiceAmount) || 0;
                monthCells[idx].feeIds.push(line.id);
                monthCells[idx].statuses.push(line.status);
            });

            const monthsRow = monthCells.map((cell) => cell.amount);
            const total = monthsRow.reduce((sum, amount) => sum + (Number(amount) || 0), 0);

            return {
                key: `${reference}-${projectName}-monthly`,
                project: projectName,
                reference,
                months: monthsRow,
                monthCells,
                total
            };
        });
    }, [all, resolvedFeeLines, selectedYear]);

    const filteredMonthlyTrackerRows = useMemo(() => {
        return monthlyTrackerRows.filter((row) => {
            const needle = searchNeedle;
            if (!needle) return true;
            return row.project.toLowerCase().includes(needle) || row.reference.toLowerCase().includes(needle);
        });
    }, [monthlyTrackerRows, searchNeedle]);

    const feeTrackerMonthIndexes = useMemo(() => {
        if (feeTrackerMonthsAhead === 'all') {
            return Array.from({ length: 12 }, (_, monthIndex) => monthIndex);
        }
        const startMonth = selectedYear === nowYear ? currentMonthIndex : 0;
        const endMonth = Math.min(11, startMonth + Math.max(1, Math.min(3, Number(feeTrackerMonthsAhead) || 3)));
        const indices = [];
        for (let monthIndex = startMonth; monthIndex <= endMonth; monthIndex += 1) {
            indices.push(monthIndex);
        }
        return indices;
    }, [selectedYear, nowYear, currentMonthIndex, feeTrackerMonthsAhead]);

    const visibleMonthlyTrackerRows = useMemo(() => {
        return filteredMonthlyTrackerRows.map((row) => ({
            ...row,
            visibleTotal: feeTrackerMonthIndexes.reduce((sum, monthIndex) => sum + (Number(row.months[monthIndex]) || 0), 0)
        }));
    }, [filteredMonthlyTrackerRows, feeTrackerMonthIndexes]);

    const monthlyTrackerTotals = useMemo(() => {
        const monthTotals = new Array(feeTrackerMonthIndexes.length).fill(0);
        let grandTotal = 0;
        visibleMonthlyTrackerRows.forEach((row) => {
            feeTrackerMonthIndexes.forEach((monthIndex, idx) => {
                monthTotals[idx] += Number(row.months[monthIndex]) || 0;
            });
            grandTotal += Number(row.visibleTotal) || 0;
        });
        return { monthTotals, grandTotal };
    }, [visibleMonthlyTrackerRows, feeTrackerMonthIndexes]);

    const invoiceManagementRows = useMemo(() => {
        const nextByReference = new Map();
        resolvedFeeLines
            .filter((line) => line.status === FEE_STATUS_FORECAST || line.status === FEE_STATUS_OVERDUE)
            .forEach((line) => {
                const key = String(line.reference || '').trim() || '—';
                const existing = nextByReference.get(key);
                if (!existing || (line.date instanceof Date && existing.date instanceof Date && line.date.getTime() < existing.date.getTime())) {
                    nextByReference.set(key, line);
                }
            });

        return Array.from(nextByReference.values())
            .map((line) => ({
                key: line.id,
                feeId: line.id,
                project: line.project,
                reference: line.reference,
                invoiceType: line.invoiceType,
                period: line.periodLabel,
                quantity: line.quantity,
                unitFee: line.unitFee,
                invoiceAmount: line.invoiceAmount,
                status: line.status,
                ruleContext: line.ruleContext,
                date: line.date
            }))
            .sort((a, b) => compareJobReferences(a.reference, b.reference) || ((a.date?.getTime?.() || 0) - (b.date?.getTime?.() || 0)));
    }, [resolvedFeeLines]);

    const filteredInvoiceManagementRows = useMemo(() => {
        return invoiceManagementRows.filter((row) => {
            const needle = searchNeedle;
            if (!needle) return true;
            return row.project.toLowerCase().includes(needle)
                || row.reference.toLowerCase().includes(needle)
                || row.invoiceType.toLowerCase().includes(needle)
                || row.status.toLowerCase().includes(needle);
        });
    }, [invoiceManagementRows, searchNeedle]);

    const invoiceManagementTotal = filteredInvoiceManagementRows.reduce((sum, row) => sum + (Number(row.invoiceAmount) || 0), 0);

    const feeLineById = useMemo(() => {
        const map = new Map();
        resolvedFeeLines.forEach((line) => {
            if (line?.id) map.set(line.id, line);
        });
        return map;
    }, [resolvedFeeLines]);

    const projectByReference = useMemo(() => {
        const map = new Map();
        all.forEach((project) => {
            const ref = String(project?.reference || '').trim();
            if (!ref) return;
            map.set(ref.toUpperCase(), project);
        });
        return map;
    }, [all]);

    function toDateInputValue(dateLike) {
        const d = dateLike instanceof Date ? dateLike : new Date(dateLike || Date.now());
        if (!Number.isFinite(d.getTime())) return '';
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    function openFeeActionMenu(event, feeIds, contextLabel, menuMeta) {
        if (!Array.isArray(feeIds) || feeIds.length === 0) return;
        const blockedStatuses = new Set([FEE_STATUS_PAID, FEE_STATUS_OVERDUE, FEE_STATUS_ISSUED]);
        const statuses = feeIds
            .map((id) => feeLineById.get(id)?.status)
            .filter(Boolean);
        const canCreateInvoice = !statuses.some((status) => blockedStatuses.has(status));
        const primaryFee = menuMeta?.primaryFee || feeLineById.get(feeIds[0]) || null;
        setFeeActionMenu({
            anchorEl: event.currentTarget,
            feeIds,
            contextLabel: contextLabel || '',
            canCreateInvoice,
            primaryFee
        });
    }

    function closeFeeActionMenu() {
        setFeeActionMenu({ anchorEl: null, feeIds: [], contextLabel: '', canCreateInvoice: false, primaryFee: null });
    }

    function updateInvoicingSetting(field, value) {
        setInvoicingSettings((prev) => ({ ...prev, [field]: value }));
    }

    function updateInvoicingAddressLine(index, value) {
        setInvoicingSettings((prev) => {
            const lines = Array.isArray(prev.address_lines) ? prev.address_lines.slice() : [];
            while (lines.length <= index) lines.push('');
            lines[index] = value;
            return { ...prev, address_lines: lines };
        });
    }

    async function saveInvoicingSettings() {
        setInvoicingSaving(true);
        setInvoicingError('');
        setInvoicingMessage('');
        try {
            const token = getAuthToken();
            if (!token) throw new Error('Missing dashboard auth token');
            const payload = {
                vat_reg_no: String(invoicingSettings.vat_reg_no || '').trim(),
                invoicing_company: String(invoicingSettings.invoicing_company || '').trim(),
                address_lines: Array.isArray(invoicingSettings.address_lines)
                    ? invoicingSettings.address_lines.map((line) => String(line || '').trim()).filter((line) => line !== '')
                    : [],
                pay_terms: String(invoicingSettings.pay_terms || '').trim(),
                bank_acc_name: String(invoicingSettings.bank_acc_name || '').trim(),
                bank_acc_nr: String(invoicingSettings.bank_acc_nr || '').trim(),
                bank_sort: String(invoicingSettings.bank_sort || '').trim(),
                invoice_sequence_next: Math.max(1, Number(invoicingSettings.invoice_sequence_next) || 1000)
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/invoicing`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const saved = json?.settings && typeof json.settings === 'object' ? json.settings : payload;
            const lines = Array.isArray(saved.address_lines) ? saved.address_lines.slice(0, 10) : [];
            while (lines.length < 4) lines.push('');
            setInvoicingSettings((prev) => ({
                ...prev,
                ...saved,
                address_lines: lines,
                template: {
                    ...(prev.template || {}),
                    ...((saved.template && typeof saved.template === 'object') ? saved.template : {})
                }
            }));
            setInvoicingMessage('Invoicing settings saved.');
        } catch (e) {
            setInvoicingError(e?.message || 'Failed to save invoicing settings');
        } finally {
            setInvoicingSaving(false);
        }
    }

    async function uploadInvoiceTemplateFile(file) {
        if (!file) return;
        setInvoicingUploading(true);
        setInvoicingError('');
        setInvoicingMessage('');
        try {
            const token = getAuthToken();
            if (!token) throw new Error('Missing dashboard auth token');
            const form = new FormData();
            form.append('file', file);

            const response = await fetch(`${API_BASE_URL}/admin/settings/invoicing/template/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: form
            });
            const json = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(json?.error || 'Failed to upload invoice template');

            const saved = json?.settings && typeof json.settings === 'object' ? json.settings : null;
            if (saved) {
                const lines = Array.isArray(saved.address_lines) ? saved.address_lines.slice(0, 10) : [];
                while (lines.length < 4) lines.push('');
                setInvoicingSettings((prev) => ({
                    ...prev,
                    ...saved,
                    address_lines: lines,
                    template: {
                        ...(prev.template || {}),
                        ...((saved.template && typeof saved.template === 'object') ? saved.template : {})
                    }
                }));
            }
            setInvoicingMessage('Invoice template uploaded.');
        } catch (e) {
            setInvoicingError(e?.message || 'Failed to upload invoice template');
        } finally {
            setInvoicingUploading(false);
        }
    }

    function openCreateInvoiceModal() {
        const line = feeActionMenu.primaryFee;
        if (!line) {
            closeFeeActionMenu();
            return;
        }

        const ref = String(line.reference || '').trim().toUpperCase();
        const project = projectByReference.get(ref) || null;
        const borrower = String(project?.borrower_company || project?.borrowerCompany || '').trim();
        const funder = String(project?.funder || project?.funder_company || '').trim();
        const invoiceeDefaultType = borrower ? 'Borrower' : 'Funder';
        const invoiceeDefault = invoiceeDefaultType === 'Borrower' ? borrower : funder;
        const baseFee = Number(line.invoiceAmount) || 0;
        const vatRate = 20;
        const vatCost = Number((baseFee * (vatRate / 100)).toFixed(2));
        const total = Number((baseFee + vatCost).toFixed(2));
        const nextInvoiceNr = Math.max(1, Number(invoicingSettings.invoice_sequence_next) || 1000);
        const reportInvoice = String(line.invoiceType || '').toLowerCase().includes('interim') ? 'Interim' : 'Initial';

        setCreateInvoiceDraft({
            feeId: String(line.id || ''),
            project: String(line.project || ''),
            reference: String(line.reference || ''),
            project_address: String(project?.site_address || project?.siteAddress || '').trim(),
            invoice_date: formatDateDdMmYyyy(new Date()),
            invoice_nr: String(nextInvoiceNr),
            invoicee_type: invoiceeDefaultType,
            borrower_name: borrower,
            funder_name: funder,
            invoicee: invoiceeDefault,
            report_invoice: reportInvoice,
            int_nr: '1',
            vat_rate: String(vatRate),
            base_fee: baseFee,
            vat_cost: vatCost,
            total
        });
        setCreateInvoiceModalOpen(true);
        closeFeeActionMenu();
    }

    function updateCreateInvoiceDraft(field, value) {
        setCreateInvoiceDraft((prev) => {
            const next = { ...prev, [field]: value };
            if (field === 'invoicee_type') {
                next.invoicee = value === 'Borrower' ? String(prev.borrower_name || '') : String(prev.funder_name || '');
            }
            if (field === 'base_fee' || field === 'vat_rate') {
                const baseFee = Number(field === 'base_fee' ? value : next.base_fee) || 0;
                const vatRate = Number(field === 'vat_rate' ? value : next.vat_rate) || 0;
                const vatCost = Number((baseFee * (vatRate / 100)).toFixed(2));
                const total = Number((baseFee + vatCost).toFixed(2));
                next.base_fee = baseFee;
                next.vat_cost = vatCost;
                next.total = total;
            }
            return next;
        });
    }

    function closeCreateInvoiceModal() {
        setCreateInvoiceError('');
        setCreateInvoiceModalOpen(false);
    }

    async function submitCreateInvoicePlaceholder() {
        const token = getAuthToken();
        if (!token) {
            setCreateInvoiceError('Missing dashboard auth token');
            return;
        }

        setCreateInvoiceSubmitting(true);
        setCreateInvoiceError('');
        setInvoicingMessage('');

        try {
            const payload = {
                ...createInvoiceDraft,
                base_fee: Number(createInvoiceDraft.base_fee) || 0,
                vat_rate: Number(createInvoiceDraft.vat_rate) || 0,
                vat_cost: Number(createInvoiceDraft.vat_cost) || 0,
                total: Number(createInvoiceDraft.total) || 0
            };

            const pdfRes = await fetch(`${API_BASE_URL}/api/financials/invoice-pdf`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!pdfRes.ok) {
                let message = 'Failed to generate invoice PDF';
                try {
                    const errJson = await pdfRes.json();
                    message = errJson?.error || message;
                } catch {
                    // ignore JSON parse failures
                }
                throw new Error(message);
            }

            const blob = await pdfRes.blob();
            const dispo = String(pdfRes.headers.get('content-disposition') || '');
            const match = dispo.match(/filename="?([^\";]+)"?/i);
            const fallbackName = `invoice-${String(createInvoiceDraft.invoice_nr || 'draft')}.pdf`;
            const fileName = (match && match[1]) ? match[1] : fallbackName;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            const nextSeq = (Number(createInvoiceDraft.invoice_nr) || Number(invoicingSettings.invoice_sequence_next) || 1000) + 1;
            try {
                const json = await fetchJson(`${API_BASE_URL}/admin/settings/invoicing`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ invoice_sequence_next: nextSeq })
                });
                const saved = json?.settings && typeof json.settings === 'object' ? json.settings : null;
                if (saved) {
                    setInvoicingSettings((prev) => ({
                        ...prev,
                        ...saved,
                        template: {
                            ...(prev.template || {}),
                            ...((saved.template && typeof saved.template === 'object') ? saved.template : {})
                        }
                    }));
                }
            } catch {
                // Keep PDF download flow non-blocking even if sequence update fails.
            }

            setInvoicingMessage('Invoice document generated and downloaded.');
            setCreateInvoiceModalOpen(false);
        } catch (e) {
            setCreateInvoiceError(e?.message || 'Failed to generate invoice PDF');
        } finally {
            setCreateInvoiceSubmitting(false);
        }
    }

    async function applyFeeAction(nextStatus) {
        if (!feeActionMenu.feeIds.length) return;
        const token = getAuthToken();
        const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
        await fetchJson(`${API_BASE_URL}/api/financials/fee-status`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ feeIds: feeActionMenu.feeIds, status: nextStatus })
        });

        try {
            const qs = new URLSearchParams({
                selected_year: String(selectedYear),
                interim_gap_months: String(interimStartGapMonths)
            });
            const json = await fetchJson(`${API_BASE_URL}/api/financials/fee-lines?${qs.toString()}`, {
                method: 'GET',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            setServerFeeLines(Array.isArray(json?.lines) ? json.lines : []);
        } catch {
            // Keep current view if refresh fails; next automatic refresh will recover.
        }
        closeFeeActionMenu();
    }

    function deriveCellStatus(statuses) {
        if (!Array.isArray(statuses) || statuses.length === 0) return '';
        if (statuses.includes(FEE_STATUS_OVERDUE)) return FEE_STATUS_OVERDUE;
        if (statuses.includes(FEE_STATUS_FORECAST)) return FEE_STATUS_FORECAST;
        if (statuses.includes(FEE_STATUS_PAID)) return FEE_STATUS_PAID;
        if (statuses.includes(FEE_STATUS_ISSUED)) return FEE_STATUS_ISSUED;
        return '';
    }

    function exportCSV() {
        const rows = [
            ['Project Name', 'Reference', 'Initial Fee', 'Interim Fee', 'Duration (months)', 'Total Fee', 'Status']
        ];
        all.forEach(p => {
            const initial = Number(p.initialReportFee) || 0;
            const interim = Number(p.interimReportFee) || 0;
            const duration = Math.max(0, Number(p.durationMonths) || 0);
            const totalFee = initial + (duration * interim);
            rows.push([
                p.name || '',
                p.reference || '',
                p.initialReportFee || '',
                p.interimReportFee || '',
                p.durationMonths || '',
                totalFee || '',
                'Active'
            ]);
        });
        const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'monsurv_financials.csv';
        a.click();
        URL.revokeObjectURL(url);
    }

    // headers according to viewMode
    const headers = (viewMode === 'monthly') ? months : (viewMode === 'quarterly') ? (selectedQuarter === 'all' ? ['Q1', 'Q2', 'Q3', 'Q4'] : [`Q${Number(selectedQuarter) + 1}`]) : [months[selectedMonthIndex]];

    const heroAccent = 'linear-gradient(135deg, rgba(47, 128, 237, 0.92), rgba(15, 23, 42, 0.88))';

    return (
        <Box className="financials" sx={{ pb: 1 }}>
            <Paper
                sx={{
                    ...premiumPanelSx,
                    mb: 2.5,
                    position: 'relative',
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, ${alpha('#ffffff', 0.94)} 0%, ${alpha('#f4f8fc', 0.88)} 44%, ${alpha('#eef5fb', 0.76)} 100%)`,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: `radial-gradient(circle at top right, ${alpha('#60a5fa', 0.16)}, transparent 36%), radial-gradient(circle at bottom left, ${alpha('#0f766e', 0.1)}, transparent 32%)`,
                        pointerEvents: 'none'
                    }
                }}
            >
                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 2.75 }, py: { xs: 2.4, sm: 3 } }}
                >
                    <Stack spacing={1.2} sx={{ maxWidth: 760 }}>
                        <Chip label="Financial Command Centre" sx={{ alignSelf: 'flex-start', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', background: heroAccent, color: '#fff' }} />
                        <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.05em', maxWidth: 620 }}>
                            Financials
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
                            <Chip icon={<CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />} label={periodLabel} variant="outlined" sx={{ fontWeight: 700, backgroundColor: alpha('#ffffff', 0.52) }} />
                            <Chip icon={<InsightsRoundedIcon sx={{ fontSize: 18 }} />} label={`${all.length} active projects`} variant="outlined" sx={{ fontWeight: 700, backgroundColor: alpha('#ffffff', 0.52) }} />
                            <Chip icon={<AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />} label={planningWindowLabel} variant="outlined" sx={{ fontWeight: 700, backgroundColor: alpha('#ffffff', 0.52) }} />
                        </Stack>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'flex-start' }}>
                        {typeof onGoToInvoiceRevenue === 'function' ? (
                            <Button variant="outlined" onClick={onGoToInvoiceRevenue} sx={{ whiteSpace: 'nowrap' }}>
                                View Invoice Revenue
                            </Button>
                        ) : null}
                        <Button variant="contained" startIcon={<DownloadRoundedIcon />} onClick={exportCSV} sx={{ whiteSpace: 'nowrap' }}>
                            Export CSV
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Paper sx={{ ...premiumPanelSx, mb: 2.5, overflow: 'hidden' }}>
                <Tabs
                    value={financialSubPage}
                    onChange={(_, value) => setFinancialSubPage(value)}
                    variant="fullWidth"
                    sx={{
                        px: { xs: 1, sm: 1.5 },
                        pt: 1,
                        '& .MuiTab-root': { fontWeight: 800, textTransform: 'none', minHeight: 44 }
                    }}
                >
                    <Tab value="fee_tracker" label="Fee Tracker" />
                    <Tab value="invoice_management" label="Invoice Management" />
                    <Tab value="system_costs" label="System Costs" />
                </Tabs>
            </Paper>

            {financialSubPage === 'fee_tracker' && (
                <PanelSection
                    eyebrow="Fee Tracker"
                    title={`Monthly tracker - ${selectedYear}`}
                    subtitle="Excel-style month-by-month fee tracker across all projects. Click a fee cell to mark Paid, Overdue, or Remove."
                    right={<Chip size="small" label={formatCurrency(monthlyTrackerTotals.grandTotal)} sx={{ fontWeight: 800, backgroundColor: alpha('#e0f2fe', 0.9), color: '#075985' }} />}
                    bodySx={{ pt: 1.5, pb: 1.6 }}
                >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
                        <TextField
                            size="small"
                            label="Search project or reference"
                            value={feeSearch}
                            onChange={(e) => setFeeSearch(e.target.value)}
                            sx={{ minWidth: { xs: '100%', md: 300 } }}
                        />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Year</InputLabel>
                            <Select label="Year" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                                {yearHints.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 210 }}>
                            <InputLabel>View</InputLabel>
                            <Select
                                label="View"
                                value={feeTrackerMonthsAhead}
                                onChange={(e) => setFeeTrackerMonthsAhead(String(e.target.value))}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="3">Current Month + 3 months</MenuItem>
                                <MenuItem value="2">Current Month + 2 months</MenuItem>
                                <MenuItem value="1">Current Month + 1 month</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Interim gap (months)"
                            type="number"
                            inputProps={{ min: 0, max: 24 }}
                            value={interimStartGapMonths}
                            onChange={(e) => setInterimStartGapMonths(Math.max(0, Math.min(24, Number(e.target.value) || 0)))}
                            sx={{ width: 170 }}
                        />
                        <Chip size="small" label={`${visibleMonthlyTrackerRows.length} projects`} sx={{ fontWeight: 800, backgroundColor: alpha('#eef4fb', 0.9), alignSelf: { xs: 'flex-start', md: 'center' } }} />
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: { md: 'auto' }, flexWrap: 'wrap' }}>
                            <Chip size="small" label="Forecast" sx={{ backgroundColor: FEE_STATUS_STYLES[FEE_STATUS_FORECAST].bg, color: FEE_STATUS_STYLES[FEE_STATUS_FORECAST].color, fontWeight: 800 }} />
                            <Chip size="small" label="Paid" sx={{ backgroundColor: FEE_STATUS_STYLES[FEE_STATUS_PAID].bg, color: FEE_STATUS_STYLES[FEE_STATUS_PAID].color, fontWeight: 800 }} />
                            <Chip size="small" label="Overdue" sx={{ backgroundColor: FEE_STATUS_STYLES[FEE_STATUS_OVERDUE].bg, color: FEE_STATUS_STYLES[FEE_STATUS_OVERDUE].color, fontWeight: 800 }} />
                            <Chip size="small" label="Issued" variant="outlined" sx={{ fontWeight: 800 }} />
                        </Stack>
                    </Stack>

                    <TableContainer sx={{ borderRadius: 4, border: `1px solid ${alpha('#0f172a', 0.06)}`, backgroundColor: alpha('#ffffff', 0.56) }}>
                        <Table stickyHeader size="small" sx={{ minWidth: (220 + 92 + (feeTrackerMonthIndexes.length * 92) + 120) }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...compactHeadCellSx, minWidth: 220, position: 'sticky', left: 0, zIndex: 5 }}>Project</TableCell>
                                    <TableCell sx={{ ...compactHeadCellSx, minWidth: 92, position: 'sticky', left: 220, zIndex: 5 }}>Ref</TableCell>
                                    {feeTrackerMonthIndexes.map((monthIndex) => (
                                        <TableCell key={months[monthIndex]} sx={{ ...compactHeadCellSx, minWidth: 92, textAlign: 'right' }}>{months[monthIndex]}</TableCell>
                                    ))}
                                    <TableCell sx={{ ...compactHeadCellSx, minWidth: 120, textAlign: 'right' }}>Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleMonthlyTrackerRows.map((row) => (
                                    <TableRow key={row.key} hover>
                                        <TableCell title={row.project} sx={{ ...compactBodyCellSx, minWidth: 220, position: 'sticky', left: 0, zIndex: 4, backgroundColor: alpha('#fbfdff', 0.95), fontWeight: 700 }}>
                                            {row.project}
                                        </TableCell>
                                        <TableCell title={row.reference} sx={{ ...compactBodyCellSx, minWidth: 92, position: 'sticky', left: 220, zIndex: 4, backgroundColor: alpha('#fbfdff', 0.95), color: 'text.secondary' }}>
                                            {row.reference}
                                        </TableCell>
                                        {feeTrackerMonthIndexes.map((monthIndex) => {
                                            const amount = row.months[monthIndex];
                                            const cell = row.monthCells[monthIndex];
                                            const status = deriveCellStatus(cell.statuses);
                                            const palette = FEE_STATUS_STYLES[status] || null;
                                            return (
                                                <TableCell
                                                    key={`${row.key}-${monthIndex}`}
                                                    onClick={(e) => openFeeActionMenu(e, cell.feeIds, `${row.project} · ${months[monthIndex]}`)}
                                                    sx={{
                                                        ...compactBodyCellSx,
                                                        minWidth: 92,
                                                        textAlign: 'right',
                                                        fontVariantNumeric: 'tabular-nums',
                                                        cursor: cell.feeIds.length ? 'pointer' : 'default',
                                                        backgroundColor: palette ? palette.bg : 'transparent',
                                                        color: palette ? palette.color : 'inherit',
                                                        fontWeight: palette ? 700 : 500,
                                                        transition: 'background-color 120ms ease',
                                                        '&:hover': cell.feeIds.length ? {
                                                            backgroundColor: (palette && status !== FEE_STATUS_ISSUED)
                                                                ? alpha(palette.bg, 0.9)
                                                                : alpha('#e2e8f0', 0.38)
                                                        } : undefined
                                                    }}
                                                    title={cell.feeIds.length ? `Click to update status (${cell.feeIds.length} fee line${cell.feeIds.length === 1 ? '' : 's'})` : 'No fee line'}
                                                >
                                                    {amount ? formatCurrency(amount) : '—'}
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell sx={{ ...compactBodyCellSx, minWidth: 120, textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                            {row.visibleTotal ? formatCurrency(row.visibleTotal) : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                <TableRow>
                                    <TableCell sx={{ ...compactBodyCellSx, minWidth: 220, position: 'sticky', left: 0, zIndex: 4, backgroundColor: alpha('#eff6ff', 0.98), fontWeight: 900 }}>
                                        Totals
                                    </TableCell>
                                    <TableCell sx={{ ...compactBodyCellSx, minWidth: 92, position: 'sticky', left: 220, zIndex: 4, backgroundColor: alpha('#eff6ff', 0.98) }}>
                                        —
                                    </TableCell>
                                    {monthlyTrackerTotals.monthTotals.map((amount, idx) => (
                                        <TableCell key={`tot-${idx}`} sx={{ ...compactBodyCellSx, minWidth: 92, textAlign: 'right', backgroundColor: alpha('#eff6ff', 0.98), fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                                            {amount ? formatCurrency(amount) : '—'}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ ...compactBodyCellSx, minWidth: 120, textAlign: 'right', backgroundColor: alpha('#eff6ff', 0.98), fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                                        {monthlyTrackerTotals.grandTotal ? formatCurrency(monthlyTrackerTotals.grandTotal) : '—'}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </PanelSection>
            )}

            {financialSubPage === 'invoice_management' && (
                <PanelSection
                    eyebrow="Invoice Management"
                    title="Invoices to be issued"
                    right={<Chip size="small" label={formatCurrency(invoiceManagementTotal)} sx={{ fontWeight: 800, backgroundColor: alpha('#fee2e2', 0.88), color: '#991b1b' }} />}
                    bodySx={{ pt: 1.5, pb: 1.6 }}
                >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
                        <TextField
                            size="small"
                            label="Search project, reference, type or status"
                            value={feeSearch}
                            onChange={(e) => setFeeSearch(e.target.value)}
                            sx={{ minWidth: { xs: '100%', md: 360 } }}
                        />
                        <Chip size="small" label={`${filteredInvoiceManagementRows.length} invoice line${filteredInvoiceManagementRows.length === 1 ? '' : 's'}`} sx={{ fontWeight: 800, backgroundColor: alpha('#eef4fb', 0.9), alignSelf: { xs: 'flex-start', md: 'center' } }} />
                    </Stack>

                    {filteredInvoiceManagementRows.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No invoices currently pending issue.</Typography>
                    ) : (
                        <TableContainer sx={{ borderRadius: 4, border: `1px solid ${alpha('#0f172a', 0.06)}`, backgroundColor: alpha('#ffffff', 0.56) }}>
                            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...compactHeadCellSx, width: '28%' }}>Project</TableCell>
                                        <TableCell sx={{ ...compactHeadCellSx, width: '9%' }}>Reference</TableCell>
                                        <TableCell sx={{ ...compactHeadCellSx, width: '18%' }}>Invoice Type</TableCell>
                                        <TableCell sx={{ ...compactHeadCellSx, width: '12%' }}>Period</TableCell>
                                        <TableCell sx={{ ...compactHeadCellSx, width: '8%', textAlign: 'right' }}>Qty</TableCell>
                                        <TableCell sx={{ ...compactHeadCellSx, width: '11%', textAlign: 'right' }}>Unit Fee</TableCell>
                                        <TableCell sx={{ ...compactHeadCellSx, width: '10%', textAlign: 'right' }}>Invoice Amount</TableCell>
                                        <TableCell sx={{ ...compactHeadCellSx, width: '8%' }}>Status</TableCell>
                                        <TableCell sx={{ ...compactHeadCellSx, width: '6%' }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredInvoiceManagementRows.map((row) => (
                                        <TableRow key={row.key} hover>
                                            <TableCell title={row.project} sx={{ ...compactBodyCellSx, fontWeight: 700 }}>{row.project}</TableCell>
                                            <TableCell title={row.reference} sx={compactBodyCellSx}>{row.reference}</TableCell>
                                            <TableCell title={row.invoiceType} sx={compactBodyCellSx}>{row.invoiceType}</TableCell>
                                            <TableCell title={row.period} sx={compactBodyCellSx}>{row.period}</TableCell>
                                            <TableCell sx={{ ...compactBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.quantity}</TableCell>
                                            <TableCell sx={{ ...compactBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(row.unitFee)}</TableCell>
                                            <TableCell sx={{ ...compactBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: '#991b1b' }}>
                                                {formatCurrency(row.invoiceAmount)}
                                            </TableCell>
                                            <TableCell sx={compactBodyCellSx}>
                                                <Chip
                                                    size="small"
                                                    label={FEE_STATUS_STYLES[row.status]?.label || row.status}
                                                    sx={{
                                                        fontWeight: 800,
                                                        backgroundColor: FEE_STATUS_STYLES[row.status]?.bg || alpha('#e2e8f0', 0.6),
                                                        color: FEE_STATUS_STYLES[row.status]?.color || '#334155'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={compactBodyCellSx}>
                                                <Button
                                                    size="small"
                                                    onClick={(e) => openFeeActionMenu(e, [row.feeId], `${row.project} · ${row.period}`, { primaryFee: row })}
                                                    sx={{ minWidth: 0, px: 1 }}
                                                >
                                                    Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </PanelSection>
            )}

            {financialSubPage === 'system_costs' && (
                <>
                    <CollapsibleSection title="Platform & AI Usage" defaultOpen={false}>
                        <PlatformUsageAnalytics />
                    </CollapsibleSection>

                    <CollapsibleSection
                        title="AWS Usage"
                        defaultOpen={false}
                        right={awsCosts ? (awsCosts.source === 'mock' ? 'Mock data' : 'Cost Explorer') : (awsCostsLoading ? 'Loading…' : '')}
                    >
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                                <Paper variant="outlined" sx={{ p: 2, minWidth: 260, flex: '0 0 auto', borderRadius: 3, backgroundColor: alpha('#ffffff', 0.58) }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>AWS Spend (Month-to-date)</Typography>
                                        <Button type="button" size="small" variant="outlined" onClick={refreshAwsCosts} disabled={awsCostsLoading}>
                                            {awsCostsLoading ? 'Refreshing…' : 'Refresh'}
                                        </Button>
                                    </Stack>
                                    {awsCostsError ? (
                                        <Typography sx={{ color: '#b91c1c', fontSize: 12 }}>{awsCostsError}</Typography>
                                    ) : null}
                                    {awsCosts ? (
                                        <>
                                            <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                                                {formatAwsCurrency(awsCosts.month_to_date_total, awsCosts.currency)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {awsCosts.source === 'mock' ? 'Mock data (AWS not enabled)' : 'Cost Explorer'}
                                                {awsCosts.fetched_at ? ` · as of ${new Date(awsCosts.fetched_at).toLocaleString()}` : ''}
                                            </Typography>
                                            {awsCosts.warning ? (
                                                <Typography variant="caption" sx={{ mt: 0.5, color: '#92400e', display: 'block' }}>{awsCosts.warning}</Typography>
                                            ) : null}
                                        </>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">{awsCostsLoading ? 'Loading…' : '—'}</Typography>
                                    )}
                                </Paper>

                                <Paper variant="outlined" sx={{ p: 2, minWidth: 260, flex: '0 0 auto', borderRadius: 3, backgroundColor: alpha('#ffffff', 0.58) }}>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>AWS Spend (Previous month)</Typography>
                                    {awsCosts ? (
                                        <>
                                            <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                                                {formatAwsCurrency(awsCosts.previous_month_total, awsCosts.currency)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">From billing month totals.</Typography>
                                        </>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">{awsCostsLoading ? 'Loading…' : '—'}</Typography>
                                    )}
                                </Paper>
                            </Stack>

                            {awsCosts && Array.isArray(awsCosts.by_service) && awsCosts.by_service.length ? (
                                <TableContainer sx={{ borderRadius: 3, border: `1px solid ${alpha('#0f172a', 0.06)}`, backgroundColor: alpha('#ffffff', 0.58) }}>
                                    <Table sx={{ minWidth: 520 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={headCellSx}>AWS Top Services (MTD)</TableCell>
                                                <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>{awsCosts.currency || 'USD'}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {awsCosts.by_service.slice(0, 10).map((row, idx) => (
                                                <TableRow key={`${row.service || 'svc'}_${idx}`} hover>
                                                    <TableCell sx={bodyCellSx}>{row.service}</TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, textAlign: 'right', fontWeight: 800 }}>
                                                        {formatAwsCurrency(row.amount, awsCosts.currency)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : null}

                            {awsCosts && Array.isArray(awsCosts.by_environment) && awsCosts.by_environment.length ? (
                                <TableContainer sx={{ borderRadius: 3, border: `1px solid ${alpha('#0f172a', 0.06)}`, backgroundColor: alpha('#ffffff', 0.58) }}>
                                    <Table sx={{ minWidth: 520 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={headCellSx}>AWS Breakdown by Environment (MTD)</TableCell>
                                                <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>{awsCosts.currency || 'USD'}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {awsCosts.by_environment.slice(0, 10).map((row, idx) => (
                                                <TableRow key={`${row.environment || 'env'}_${idx}`} hover>
                                                    <TableCell sx={bodyCellSx}>{row.environment}</TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, textAlign: 'right', fontWeight: 800 }}>
                                                        {formatAwsCurrency(row.amount, awsCosts.currency)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : null}
                        </Box>
                    </CollapsibleSection>
                </>
            )}

            <Dialog open={createInvoiceModalOpen} onClose={closeCreateInvoiceModal} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Create Invoice</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Review the invoice details below. On Create Invoice, a PDF will be generated and downloaded automatically.
                        </Typography>
                        {createInvoiceError ? (
                            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
                                {createInvoiceError}
                            </Typography>
                        ) : null}
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <TextField label="Invoice date {Invoice Date}" size="small" value={createInvoiceDraft.invoice_date} onChange={(e) => updateCreateInvoiceDraft('invoice_date', e.target.value)} sx={{ width: { md: 220 } }} />
                            <TextField label="Invoice No. {invoice_nr}" size="small" value={createInvoiceDraft.invoice_nr} onChange={(e) => updateCreateInvoiceDraft('invoice_nr', e.target.value)} sx={{ width: { md: 220 } }} />
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel>Invoice Addressee {`{Invoicee}`}</InputLabel>
                                <Select
                                    label="Invoice Addressee {Invoicee}"
                                    value={createInvoiceDraft.invoicee_type}
                                    onChange={(e) => updateCreateInvoiceDraft('invoicee_type', e.target.value)}
                                >
                                    <MenuItem value="Borrower">Borrower</MenuItem>
                                    <MenuItem value="Funder">Funder</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField label="Invoice Addressee Text {Invoicee}" size="small" value={createInvoiceDraft.invoicee} onChange={(e) => updateCreateInvoiceDraft('invoicee', e.target.value)} fullWidth />
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <TextField label="Project Reference No. {Proj_Ref}" size="small" value={createInvoiceDraft.reference} fullWidth disabled />
                            <TextField label="Project Address {Project_Address}" size="small" value={createInvoiceDraft.project_address} onChange={(e) => updateCreateInvoiceDraft('project_address', e.target.value)} fullWidth />
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel>Report Invoice {`{Report_Invoice}`}</InputLabel>
                                <Select
                                    label="Report Invoice {Report_Invoice}"
                                    value={createInvoiceDraft.report_invoice}
                                    onChange={(e) => updateCreateInvoiceDraft('report_invoice', e.target.value)}
                                >
                                    <MenuItem value="Initial">Initial</MenuItem>
                                    <MenuItem value="Interim">Interim</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Interim Nr {`{int_nr}`}</InputLabel>
                                <Select
                                    label="Interim Nr {int_nr}"
                                    value={createInvoiceDraft.int_nr}
                                    onChange={(e) => updateCreateInvoiceDraft('int_nr', e.target.value)}
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                                        <MenuItem key={`int-${n}`} value={String(n)}>{n}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>VAT {`{VAT}`}</InputLabel>
                                <Select
                                    label="VAT {VAT}"
                                    value={createInvoiceDraft.vat_rate}
                                    onChange={(e) => updateCreateInvoiceDraft('vat_rate', e.target.value)}
                                >
                                    <MenuItem value="20">20%</MenuItem>
                                    <MenuItem value="5">5%</MenuItem>
                                    <MenuItem value="0">0%</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Invoice Amount EX VAT {base_fee}"
                                size="small"
                                type="number"
                                value={createInvoiceDraft.base_fee}
                                onChange={(e) => updateCreateInvoiceDraft('base_fee', e.target.value)}
                                fullWidth
                            />
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <TextField label="VAT Calculation {VAT_Cost}" size="small" value={formatCurrency(createInvoiceDraft.vat_cost)} fullWidth disabled />
                            <TextField label="Total Fee {total}" size="small" value={formatCurrency(createInvoiceDraft.total)} fullWidth disabled />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.25 }}>
                    <Button onClick={closeCreateInvoiceModal} disabled={createInvoiceSubmitting}>Cancel</Button>
                    <Button variant="contained" onClick={submitCreateInvoicePlaceholder} disabled={createInvoiceSubmitting}>
                        {createInvoiceSubmitting ? 'Generating PDF...' : 'Create Invoice'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={feeActionMenu.anchorEl}
                open={Boolean(feeActionMenu.anchorEl)}
                onClose={closeFeeActionMenu}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                {feeActionMenu.contextLabel ? (
                    <MenuItem disabled sx={{ opacity: 0.72, fontWeight: 700 }}>
                        {feeActionMenu.contextLabel}
                    </MenuItem>
                ) : null}
                {feeActionMenu.canCreateInvoice ? (
                    <MenuItem onClick={openCreateInvoiceModal}>Create Invoice</MenuItem>
                ) : null}
                <MenuItem onClick={() => applyFeeAction(FEE_STATUS_PAID)}>Paid</MenuItem>
                <MenuItem onClick={() => applyFeeAction(FEE_STATUS_ISSUED)}>Issued</MenuItem>
                <MenuItem onClick={() => applyFeeAction(FEE_STATUS_OVERDUE)}>Overdue</MenuItem>
                <MenuItem onClick={() => applyFeeAction(FEE_STATUS_REMOVED)} sx={{ color: '#b91c1c' }}>Remove</MenuItem>
            </Menu>
        </Box>
    );
}
